import { MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { useCallback } from "react"
import {
  Control,
  FieldErrors,
  Path,
  PathValue,
  UseFormSetValue,
  useWatch,
} from "react-hook-form"
import {
  ApplicationPropertyDetail,
  ApplicationPropertyPossibleValue,
  ApplicationPropertyTypeEnum,
} from "../../models/ApplicationProperty"

type BaseForm = { propertyName: string; propertyValue: string }

interface PropertyValueProps<TForm extends BaseForm> {
  control: Control<TForm>
  setValue: UseFormSetValue<TForm>
  applicationRolePermissionPropertiesIds: ApplicationPropertyDetail[]
  propertyPossibleValues: Record<string, ApplicationPropertyPossibleValue[]>
  propertyType: ApplicationPropertyTypeEnum
  propertyPossibleValue: ApplicationPropertyPossibleValue
  setPropertyPossibleValue: (value: ApplicationPropertyPossibleValue) => void
  errors: FieldErrors<TForm>
}

export function PropertyValue<TForm extends BaseForm>({
  control,
  setValue,
  applicationRolePermissionPropertiesIds,
  propertyPossibleValues,
  propertyType,
  propertyPossibleValue,
  setPropertyPossibleValue,
  errors,
}: Readonly<PropertyValueProps<TForm>>) {
  const propertyNameKey = "propertyName" as Path<TForm>
  const propertyValueKey = "propertyValue" as Path<TForm>

  const propertyName = useWatch({ control, name: propertyNameKey }) || ""
  const propertyValue = useWatch({ control, name: propertyValueKey }) || ""

  const handleTextFieldChange = useCallback(
    (value: string) => {
      setValue(propertyValueKey, value as PathValue<TForm, typeof propertyValueKey>, {
        shouldValidate: true,
      })
    },
    [setValue]
  )

  const handleAutocompleteGetOptionLabel = useCallback(
    (o: ApplicationPropertyPossibleValue) => o.label,
    []
  )

  const handleAutocompleteGetOptionDisabled = useCallback(
    (o: ApplicationPropertyPossibleValue) =>
      applicationRolePermissionPropertiesIds.some(
        (p) => p.name === propertyName && p.value === o.value
      ),
    [applicationRolePermissionPropertiesIds, propertyName]
  )

  const dropdownOptions = (propertyPossibleValues[propertyName] ?? []).map((opt) => ({
    id: opt.value,
    name: handleAutocompleteGetOptionLabel(opt) ?? "",
    isDisabled: handleAutocompleteGetOptionDisabled(opt),
  }))

  const handleAutocompleteChange = useCallback(
    (next: Array<string | { id: string }>) => {
      const firstItem = next?.[0]
      const selectedId = typeof firstItem === "string" ? firstItem : firstItem?.id
      if (!selectedId) {
        return
      }

      setValue(
        propertyValueKey,
        selectedId as PathValue<TForm, typeof propertyValueKey>,
        {
          shouldValidate: true,
        }
      )

      const selectedOption = (propertyPossibleValues[propertyName] ?? []).find(
        (x) => x.value === selectedId
      )
      if (selectedOption) {
        setPropertyPossibleValue(selectedOption)
      }
    },
    [
      setValue,
      propertyValueKey,
      propertyPossibleValues,
      propertyName,
      setPropertyPossibleValue,
    ]
  )

  const selectedValue = propertyPossibleValue?.value || propertyValue || ""

  if (propertyType === ApplicationPropertyTypeEnum.String) {
    return (
      <MUITextField
        size={32}
        placeholder="Enter a value"
        value={propertyValue}
        onChange={handleTextFieldChange}
        errors={
          typeof errors.propertyValue?.message === "string"
            ? { propertyValue: { message: errors.propertyValue.message } }
            : undefined
        }
      />
    )
  }

  return (
    <MUISelect
      name="propertyValue"
      placeholder="Select value"
      size={32}
      dropdownLOV={dropdownOptions}
      enableSearch
      clearable={false}
      isDisabled={!propertyName}
      selectedItem={selectedValue ? [selectedValue] : []}
      onChange={handleAutocompleteChange}
    />
  )
}

export default PropertyValue
